import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, HeartPulse, ShieldCheck, Stethoscope, Users, Wind } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const steps = [
  { icon: Stethoscope, title: "Apply with your license", text: "Clinicians submit license number, type and state. We review every application by hand." },
  { icon: ShieldCheck, title: "Get approved, choose a plan", text: "Approved clinicians set a password and pick a plan. The app unlocks the moment payment clears." },
  { icon: Users, title: "Invite your clients", text: "Add client emails from your dashboard. Clients are free and tied to you — no public sign-ups." },
  { icon: HeartPulse, title: "Practise between sessions", text: "Guided breathing with mood check-ins, progress and a gentle streak. You see practice totals; moods stay private." },
];

const faqs = [
  ["Who can use the app?", "Licensed therapists, therapist associates and the clients they invite. There is no public registration."],
  ["How is a license verified?", "An administrator reviews each application against the state licensing board lookup before approval."],
  ["Does it work on VR headsets?", "Yes, as a web app in the headset browser (Meta Quest Browser, Pico Browser, Apple Vision Pro Safari). It is not a fully immersive 3D experience."],
  ["Does it use AI on client data?", "No. Progress, sessions and mood check-ins are stored and displayed as-is; nothing is sent to an AI model."],
  ["Can I cancel?", "Yes, any time from Billing. Access continues until the end of the paid period."],
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="landing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6 rise">
          <p className="eyebrow">For licensed clinicians</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-slate-900">
            Guided breathing and mindfulness your clients <span className="text-indigo-600">actually practise.</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
            Tutorials gives therapists an evidence-based library of interactive breathing exercises, mindfulness guides and in-room scripts for CBT, DBT and Gestalt work — with private practice tracking for every client.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-7"><Link to="/apply" data-testid="hero-apply-button">Apply for clinician access <ArrowRight className="w-4 h-4" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7"><Link to="/login" data-testid="hero-login-button">I already have an account</Link></Button>
          </div>
          <p className="text-xs text-slate-500">Clients join by invitation from their therapist — free, private, no card needed.</p>
        </div>
        <div className="lg:col-span-5 rise" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-[2rem] bg-slate-900 text-white p-8 overflow-hidden grain">
            <Wind className="absolute -right-10 -bottom-10 w-56 h-56 text-white/5" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-300">Inside the library</p>
            <ul className="mt-5 space-y-3 text-sm">
              {["Box, 4-7-8, calm and equal breathing with voice cues", "Mood check-in before and after every session", "Clinician guides: CBT · DBT · Gestalt scripts", "Peer-reviewed evidence summaries", "Client dashboard with minutes, sessions and last active"].map((t) => (
                <li key={t} className="flex gap-3"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <p className="eyebrow mb-3">How it works</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-10">From application to the therapy room in four steps</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <li key={s.title} className="rounded-3xl border border-slate-200 p-6 space-y-3">
                <span className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 grid place-items-center"><s.icon className="w-5 h-5" /></span>
                <p className="text-xs font-bold text-slate-400">Step {i + 1}</p>
                <h3 className="font-display font-bold text-slate-900">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <p className="eyebrow mb-3">Pricing</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Simple plans for clinicians. Clients are always free.</h2>
        <p className="text-slate-600 mb-10">Billed through Stripe. Cancel any time.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {plans.map((p) => (
            <div key={p.lookup_key} className={`rounded-3xl p-7 border flex flex-col ${p.highlight ? "bg-indigo-600 text-white border-indigo-600 shadow-xl" : "bg-white border-slate-200"}`} data-testid={`plan-card-${p.lookup_key}`}>
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${p.highlight ? "text-indigo-200" : "text-indigo-600"}`}>{p.name}</p>
              <p className="font-display text-4xl font-extrabold mt-3">${p.price}<span className={`text-base font-medium ${p.highlight ? "text-indigo-200" : "text-slate-500"}`}>/{p.interval}</span></p>
              <p className={`text-sm mt-2 ${p.highlight ? "text-indigo-100" : "text-slate-600"}`}>{p.blurb}</p>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {p.features.map((f) => <li key={f} className="flex gap-2"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-emerald-300" : "text-emerald-600"}`} />{f}</li>)}
              </ul>
              <Button asChild className={`mt-6 rounded-full ${p.highlight ? "bg-white text-indigo-700 hover:bg-indigo-50" : "bg-slate-900 text-white hover:bg-slate-800"}`}><Link to="/apply">Apply to subscribe</Link></Button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <p className="eyebrow mb-3">Questions</p>
          <dl className="divide-y divide-slate-100">
            {faqs.map(([q, a]) => (
              <div key={q} className="py-5"><dt className="font-display font-bold text-slate-900">{q}</dt><dd className="text-sm text-slate-600 mt-1 leading-relaxed">{a}</dd></div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}
